// ─── Chapter Workflow Engine ──────────────────────────────────────────────────
// Enforces the editorial state machine: DRAFT → UNDER_REVIEW → APPROVED → LOCKED
// All transitions write to thesis_audit_log. Illegal transitions throw.

import { supabase } from '../lib/supabase'

// ── Valid states ──────────────────────────────────────────────────────────────

export const WORKFLOW_STATES = {
  DRAFT:        'DRAFT',
  UNDER_REVIEW: 'UNDER_REVIEW',
  APPROVED:     'APPROVED',
  LOCKED:       'LOCKED',
}

// ── Allowed forward transitions ───────────────────────────────────────────────

const ALLOWED_TRANSITIONS = {
  DRAFT:        ['UNDER_REVIEW'],
  UNDER_REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED:     ['LOCKED', 'DRAFT'],
  LOCKED:       ['APPROVED'],
  // Legacy/compat states — always allow moving to DRAFT
  NOT_STARTED:  ['DRAFT'],
  GENERATING:   ['DRAFT'],
  REVIEWED:     ['UNDER_REVIEW', 'DRAFT'],
  REVIEW:       ['UNDER_REVIEW', 'DRAFT'],
  FINALIZED:    ['APPROVED', 'DRAFT'],
  REGEN_PENDING:['DRAFT'],
}

// ── Editing-locked states (no content changes allowed) ────────────────────────

export const EDITING_LOCKED_STATES = new Set(['APPROVED', 'LOCKED'])

// ── States that block AI regeneration ────────────────────────────────────────

export const REGEN_BLOCKED_STATES = new Set(['APPROVED', 'LOCKED'])

// ── States that block thesis export ──────────────────────────────────────────

export const EXPORT_BLOCKING_STATES = new Set(['DRAFT', 'UNDER_REVIEW'])

// ── Helpers ───────────────────────────────────────────────────────────────────

function isValidTransition(from, to) {
  const allowed = ALLOWED_TRANSITIONS[from] || []
  return allowed.includes(to)
}

function simpleHash(str) {
  if (!str) return ''
  let h = 5381
  for (let i = 0; i < Math.min(str.length, 8000); i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i)
  }
  return (h >>> 0).toString(36)
}

async function getUserEmail() {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.user?.email || 'unknown'
  } catch {
    return 'unknown'
  }
}

async function writeAuditLog({ thesisId, chapterId, previousState, newState, reason, metadata = {} }) {
  const userEmail = await getUserEmail()
  try {
    await supabase.from('thesis_audit_log').insert({
      thesis_id:      thesisId,
      chapter_id:     chapterId || null,
      previous_state: previousState,
      new_state:      newState,
      user_email:     userEmail,
      reason:         reason || null,
      metadata,
    })
  } catch (err) {
    console.warn('[chapterWorkflowEngine] audit log insert failed:', err.message)
  }
}

// ── Core transition function ──────────────────────────────────────────────────

/**
 * Apply a state transition to a chapter.
 * Validates the transition, updates the DB, and writes an audit log entry.
 *
 * @param {{ thesisId, chapterId, currentStatus, targetStatus, reason?, contentHtml? }} params
 * @returns {Promise<{ status: string }>} the new status
 * @throws if the transition is invalid
 */
export async function applyWorkflowTransition({
  thesisId,
  chapterId,
  currentStatus,
  targetStatus,
  reason = '',
  contentHtml = '',
}) {
  const from = currentStatus || 'DRAFT'
  const to   = targetStatus

  if (!isValidTransition(from, to)) {
    throw new Error(`Invalid transition: ${from} → ${to}`)
  }

  const now       = new Date().toISOString()
  const userEmail = await getUserEmail()
  const hash      = contentHtml ? simpleHash(contentHtml) : undefined

  const update = { status: to }

  if (to === 'APPROVED') {
    update.approved_at = now
    update.approved_by = userEmail
    if (hash) update.content_hash = hash
    update.last_validated_at = now
  }

  if (to === 'LOCKED') {
    update.locked_at = now
    update.locked_by = userEmail
    // content_hash at lock time freezes numbering — exportCompositionEngine uses
    // this hash to detect if content has drifted from the locked state.
    if (hash) update.content_hash = hash
  }

  if (to === 'DRAFT') {
    // Revert: clear approval/lock timestamps
    update.approved_at = null
    update.approved_by = null
    if (hash) update.content_hash = hash
  }

  if (to === 'UNDER_REVIEW' && hash) {
    update.content_hash = hash
  }

  const { error } = await supabase
    .from('chapters')
    .update(update)
    .eq('id', chapterId)

  if (error) throw new Error(`DB update failed: ${error.message}`)

  await writeAuditLog({
    thesisId,
    chapterId,
    previousState: from,
    newState:      to,
    reason:        reason || `Transitioned from ${from} to ${to}`,
    metadata:      { userEmail, timestamp: now },
  })

  return { status: to }
}

// ── Auto-invalidate approval on content edit ──────────────────────────────────

/**
 * Called whenever chapter content changes. If the chapter is APPROVED, reverts
 * to DRAFT and logs the invalidation.
 *
 * @returns {Promise<string|null>} the new status if changed, null if unchanged
 */
export async function invalidateApprovalOnEdit({ thesisId, chapterId, currentStatus, contentHtml }) {
  if (currentStatus !== 'APPROVED') return null
  if (currentStatus === 'LOCKED') return null // LOCKED is immutable via edit gate

  await applyWorkflowTransition({
    thesisId,
    chapterId,
    currentStatus,
    targetStatus: 'DRAFT',
    reason:       'Approval invalidated — content was edited after approval',
    contentHtml,
  })

  return 'DRAFT'
}

// ── Audit log reader ──────────────────────────────────────────────────────────

/**
 * Load audit history for a chapter, most recent first.
 * @returns {Promise<Array>}
 */
export async function loadChapterAuditLog(chapterId) {
  const { data, error } = await supabase
    .from('thesis_audit_log')
    .select('*')
    .eq('chapter_id', chapterId)
    .order('created_at', { ascending: false })
    .limit(20)

  if (error) return []
  return data || []
}

// ── Export readiness cache ────────────────────────────────────────────────────
// Keyed by a string of "chapterId:status" for each chapter. Invalidated whenever
// any status changes — avoids redundant Supabase queries on rapid re-checks.

const _readinessCache = new Map() // cacheKey → { result, expiresAt }
const CACHE_TTL_MS    = 30_000   // 30 seconds

function _readinessCacheKey(chapters) {
  return chapters.map(c => `${c.id}:${c.status}`).join('|')
}

// ── Export gate check ─────────────────────────────────────────────────────────

/**
 * Check if all chapters are ready for export (all must be APPROVED or LOCKED).
 * Returns { exportReady: boolean, blockingChapters: Array }
 * Results are cached for 30 s — only re-fetches when chapter statuses change.
 */
export async function checkExportReadiness(thesisId) {
  const { data: chapters, error } = await supabase
    .from('chapters')
    .select('id, title, chapter_number, status')
    .eq('thesis_id', thesisId)
    .order('chapter_number')

  if (error || !chapters?.length) {
    return { exportReady: false, blockingChapters: [], error: error?.message }
  }

  const cacheKey = _readinessCacheKey(chapters)
  const cached   = _readinessCache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) return cached.result

  const blocking = chapters.filter(ch => EXPORT_BLOCKING_STATES.has(ch.status || 'DRAFT'))
  const result = {
    exportReady:      blocking.length === 0,
    blockingChapters: blocking,
    totalChapters:    chapters.length,
    approvedCount:    chapters.filter(ch => !EXPORT_BLOCKING_STATES.has(ch.status || 'DRAFT')).length,
  }

  _readinessCache.set(cacheKey, { result, expiresAt: Date.now() + CACHE_TTL_MS })
  return result
}
